import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import { locationApi } from '../services/api';
import { Location, Employee } from '../types';
import { isLocationActive } from '../utils/locationDisplay';
import { useAuth } from './AuthContext';

const LOCATIONS_CACHE_KEY = 'locationsCache';
const LOCATIONS_SESSION_KEY = 'staffly:locationsSession';

export interface LocationContextType {
  /** Immediate selection — use for header UI and localStorage. */
  selectedLocationId: number | null;
  setSelectedLocationId: (id: number | null) => void;
  /** Debounced selection — prefer for API calls and heavy page filters. */
  debouncedSelectedLocationId: number | null;
  locations: Location[];
  /** True only when there are no locations to show yet (first load, no cache). */
  locationsLoading: boolean;
  /** Background refresh while cached locations remain visible. */
  locationsRefreshing: boolean;
  selectedLocation: Location | null;
  isSelectedLocationInactive: boolean;
  employeeDetailsReportLocationLocked: boolean;
  lockEmployeeDetailsReportLocation: () => void;
  unlockEmployeeDetailsReportLocation: () => void;
  reloadLocations: () => Promise<void>;
}

const LocationContext = createContext<LocationContextType | undefined>(undefined);

interface LocationsSnapshot {
  locations: Location[];
  selectedLocationId: number | null;
  employeeId: number;
  updatedAt: number;
}

function readStoredEmployeeId(): number | null {
  try {
    const saved = localStorage.getItem('employee');
    if (!saved) return null;
    const id = JSON.parse(saved)?.id;
    return typeof id === 'number' ? id : null;
  } catch (_) {
    return null;
  }
}

function readSessionSnapshot(employeeId?: number | null): LocationsSnapshot | null {
  try {
    const raw = sessionStorage.getItem(LOCATIONS_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LocationsSnapshot;
    if (!parsed.locations?.length || !parsed.employeeId) return null;
    if (employeeId != null && parsed.employeeId !== employeeId) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function readCachedLocations(employeeId?: number | null): Location[] {
  const session = readSessionSnapshot(employeeId);
  if (session?.locations.length) {
    return session.locations;
  }

  try {
    const cached = localStorage.getItem(LOCATIONS_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as {
        locations?: Location[];
        employeeId?: number;
      };
      const cachedLocations = parsed.locations;
      const cachedEmployeeId = parsed.employeeId;
      if (
        cachedLocations?.length &&
        (employeeId == null || cachedEmployeeId == null || cachedEmployeeId === employeeId)
      ) {
        return cachedLocations;
      }
    }
  } catch (_) {
    /* ignore */
  }
  return [];
}

function persistLocationsSnapshot(
  locations: Location[],
  selectedLocationId: number | null,
  employeeId: number
): void {
  const snapshot: LocationsSnapshot = {
    locations,
    selectedLocationId,
    employeeId,
    updatedAt: Date.now(),
  };
  try {
    sessionStorage.setItem(LOCATIONS_SESSION_KEY, JSON.stringify(snapshot));
    localStorage.setItem(
      LOCATIONS_CACHE_KEY,
      JSON.stringify({
        locations,
        employeeId,
        timestamp: Date.now(),
      })
    );
  } catch (_) {
    /* ignore */
  }
}

function readDeepLinkLocationId(): number | null {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('locationId');
  if (!raw) return null;
  const id = parseInt(raw, 10);
  return Number.isNaN(id) ? null : id;
}

function readInitialSelectedLocationId(
  locations: Location[],
  employeeId?: number | null
): number | null {
  const deepLinkLocationId = readDeepLinkLocationId();
  if (deepLinkLocationId != null) {
    if (locations.length === 0 || locations.some((l) => l.id === deepLinkLocationId)) {
      return deepLinkLocationId;
    }
  }

  const session = readSessionSnapshot(employeeId);
  if (session?.selectedLocationId != null) {
    if (locations.length === 0 || locations.some((l) => l.id === session.selectedLocationId)) {
      return session.selectedLocationId;
    }
  }

  const saved = localStorage.getItem('selectedLocationId');
  const savedId = saved ? Number(saved) : null;
  if (savedId && !Number.isNaN(savedId)) {
    if (locations.length === 0 || locations.some((l) => l.id === savedId)) return savedId;
  }
  if (locations.length > 0) return locations[0].id;
  try {
    const savedEmployee = localStorage.getItem('employee');
    if (savedEmployee) {
      const mainId = JSON.parse(savedEmployee)?.profile?.mainLocationId;
      if (typeof mainId === 'number') {
        if (locations.length === 0 || locations.some((l) => l.id === mainId)) return mainId;
      }
    }
  } catch (_) {
    /* ignore */
  }
  return null;
}

function hasPersistedLocationSelection(): boolean {
  if (readDeepLinkLocationId() != null) return true;
  try {
    const saved = localStorage.getItem('selectedLocationId');
    if (saved && !Number.isNaN(Number(saved))) return true;
  } catch (_) {
    /* ignore */
  }
  return false;
}

function resolveSelectedLocationId(
  locationsData: Location[],
  emp: Employee,
  currentId: number | null
): number | null {
  if (locationsData.length === 0) return null;

  if (currentId && locationsData.some((loc) => loc.id === currentId)) return currentId;

  const savedLocationId = localStorage.getItem('selectedLocationId');
  const savedId = savedLocationId ? Number(savedLocationId) : null;
  const mainLocationId = emp.profile?.mainLocationId;

  if (savedId && locationsData.some((loc) => loc.id === savedId)) return savedId;
  if (mainLocationId && locationsData.some((loc) => loc.id === mainLocationId)) return mainLocationId;
  return locationsData[0].id;
}

export const LocationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { employee } = useAuth();
  const storedEmployeeId = readStoredEmployeeId();
  const cacheEmployeeId = employee?.id ?? storedEmployeeId;
  const initialCached = readCachedLocations(cacheEmployeeId);
  const initialSelectedId = readInitialSelectedLocationId(initialCached, cacheEmployeeId);

  const [locations, setLocations] = useState<Location[]>(initialCached);
  const [selectedLocationId, setSelectedLocationIdState] = useState<number | null>(initialSelectedId);
  const [locationsLoading, setLocationsLoading] = useState(
    () => initialCached.length === 0 && !hasPersistedLocationSelection()
  );
  const [locationsRefreshing, setLocationsRefreshing] = useState(false);
  const [employeeDetailsReportLocationLocked, setEmployeeDetailsReportLocationLocked] =
    useState(false);
  const selectedLocationIdRef = useRef<number | null>(initialSelectedId);
  const savedLocationBeforeEmployeeDetailsReportRef = useRef<number | null>(null);
  const reloadInFlightRef = useRef(false);

  const debouncedSelectedLocationId = selectedLocationId;

  const setSelectedLocationId = useCallback((id: number | null) => {
    setSelectedLocationIdState(id);
    selectedLocationIdRef.current = id;
  }, []);

  useEffect(() => {
    selectedLocationIdRef.current = selectedLocationId;
  }, [selectedLocationId]);

  const lockEmployeeDetailsReportLocation = useCallback(() => {
    savedLocationBeforeEmployeeDetailsReportRef.current = selectedLocationIdRef.current;
    setEmployeeDetailsReportLocationLocked(true);
    setSelectedLocationIdState(null);
    selectedLocationIdRef.current = null;
  }, []);

  const unlockEmployeeDetailsReportLocation = useCallback(() => {
    setEmployeeDetailsReportLocationLocked(false);
    const id = savedLocationBeforeEmployeeDetailsReportRef.current;
    savedLocationBeforeEmployeeDetailsReportRef.current = null;
    setSelectedLocationIdState(id);
    selectedLocationIdRef.current = id;
  }, []);

  const reloadLocations = useCallback(async () => {
    if (!employee?.id) {
      if (locations.length === 0) {
        setLocations([]);
        setSelectedLocationIdState(null);
        selectedLocationIdRef.current = null;
      }
      setLocationsLoading(false);
      setLocationsRefreshing(false);
      return;
    }

    if (reloadInFlightRef.current) return;
    reloadInFlightRef.current = true;

    const hasVisibleLocations = locations.length > 0;
    if (!hasVisibleLocations && !hasPersistedLocationSelection()) {
      setLocationsLoading(true);
    } else {
      setLocationsRefreshing(true);
    }

    try {
      const locationsData = await locationApi.getMine();

      setLocations(locationsData);

      const nextId = resolveSelectedLocationId(
        locationsData,
        employee,
        selectedLocationIdRef.current
      );
      setSelectedLocationIdState(nextId);
      selectedLocationIdRef.current = nextId;

      persistLocationsSnapshot(locationsData, nextId, employee.id);
    } catch (error) {
      console.error('Error fetching locations:', error);
    } finally {
      setLocationsLoading(false);
      setLocationsRefreshing(false);
      reloadInFlightRef.current = false;
    }
  }, [employee, locations.length]);

  useEffect(() => {
    reloadLocations();
  }, [employee?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedLocationId) {
      localStorage.setItem('selectedLocationId', selectedLocationId.toString());
    }
    if (employee?.id && locations.length > 0) {
      persistLocationsSnapshot(locations, selectedLocationId, employee.id);
    }
  }, [selectedLocationId, locations, employee?.id]);

  const selectedLocation = useMemo(
    () =>
      selectedLocationId != null
        ? locations.find((l) => l.id === selectedLocationId) ?? null
        : null,
    [locations, selectedLocationId]
  );

  const isSelectedLocationInactive = useMemo(
    () => selectedLocation != null && !isLocationActive(selectedLocation),
    [selectedLocation]
  );

  const value = useMemo<LocationContextType>(
    () => ({
      selectedLocationId,
      setSelectedLocationId,
      debouncedSelectedLocationId,
      locations,
      locationsLoading,
      locationsRefreshing,
      selectedLocation,
      isSelectedLocationInactive,
      employeeDetailsReportLocationLocked,
      lockEmployeeDetailsReportLocation,
      unlockEmployeeDetailsReportLocation,
      reloadLocations,
    }),
    [
      selectedLocationId,
      setSelectedLocationId,
      debouncedSelectedLocationId,
      locations,
      locationsLoading,
      locationsRefreshing,
      selectedLocation,
      isSelectedLocationInactive,
      employeeDetailsReportLocationLocked,
      lockEmployeeDetailsReportLocation,
      unlockEmployeeDetailsReportLocation,
      reloadLocations,
    ]
  );

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

export function useLocationContext(): LocationContextType {
  const context = useContext(LocationContext);
  if (context === undefined) {
    throw new Error('useLocationContext must be used within a LocationProvider');
  }
  return context;
}
